<template>
  <svg v-if="showSvg" class="dependencies-svg">
    <g v-for="dependenciesLink in dependenciesLinks"
       :class="[{ 'invalid-dependency': dependenciesLink.invalid },
                { 'critical-path': ! dependenciesLink.invalid && displayCriticalPath && dependenciesLink.isCriticalPath }]">
      <path :d="dependenciesLink.linePath" class="dependency-path" />
    </g>
  </svg>
  <svg v-if="showSvg" class="dependencies-svg" style="z-index: 105">
    <g v-for="dependenciesLink in dependenciesLinks"
       :class="[{ 'invalid-dependency': dependenciesLink.invalid },
                { 'critical-path': ! dependenciesLink.invalid && displayCriticalPath && dependenciesLink.isCriticalPath }]">
      <circle :cx="dependenciesLink.el.x" :cy="dependenciesLink.el.y" r="4" class="dependency-startpoint" />
      <path :d="dependenciesLink.arrowPath" class="dependency-endarrow" />
    </g>
  </svg>

  <svg v-if="draggingLink" class="dependencies-svg" style="z-index: 115">
    <g :class="[{ 'invalid-dependency': draggingLink.invalid },
                { 'critical-path': ! draggingLink.invalid && displayCriticalPath && draggingLink.isCriticalPath }]">
      <path :d="draggingLink.linePath" class="dependency-path" style="stroke-dasharray: 6,4" />
      <circle :cx="draggingLink.el.x" :cy="draggingLink.el.y" r="4" class="dependency-startpoint" />
      <path :d="draggingLink.arrowPath" class="dependency-endarrow" />
    </g>
  </svg>
</template>

<style lang="scss">
  .dependencies-svg {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    pointer-events: none;

    .dependency-path {
      fill: transparent;
      stroke:rgba(55, 55, 55, .5);
      stroke-width: 1.75;
    }
    .dependency-startpoint {
      fill: rgba(55, 55, 55, .9);
    }
    .dependency-endarrow {
      fill: rgba(55, 55, 55, .9);
      stroke:rgba(55, 55, 55, .5);
      stroke-width: 1;
    }

    .v-theme--dark & {
      .dependency-path {
        stroke:rgba(255, 255, 255, .5);
      }
      .dependency-startpoint {
        fill: rgba(255, 255, 255, .9);
      }
      .dependency-endarrow {
        fill: rgba(255, 255, 255, .9);
        stroke:rgba(255, 255, 255, .5);
      }
    }

    .invalid-dependency {
      .dependency-path {
        stroke:rgba(255, 0, 0, .5);
      }
      .dependency-startpoint {
        fill: rgba(255, 0, 0, .9);
      }
      .dependency-endarrow {
        fill: rgba(255, 0, 0, .9);
        stroke:rgba(255, 0, 0, .5);
      }
    }

    .critical-path {
      .dependency-path {
        stroke:rgba(255, 190, 51);
      }
      .dependency-startpoint {
        fill: rgba(255, 190, 51);
      }
      .dependency-endarrow {
        fill: rgba(255, 190, 51);
        stroke:rgba(255, 190, 51);
      }
    }
  }
</style>

<script>
  import useDependenciesStore from '@/stores/dependencies';
  import useUiPlanningStore from '@/stores/ui/planning';
  import usePlanningStore from '@/stores/planning/planning';
  import usePlanningLanesStore from '@/stores/planning/lanes';
  import useSubprojectsStore from '@/stores/subprojects';
  import constants from '@/js/constants';
  import { getAnchorTime, getDependencyAnchors } from './dependencyHelpers';

  let $tableBody; // svg parent element
  let $tableWrapper; // scroll element

  export default {
    props: {
      planning: { type: Object, required: true },
    },
    data() {
      return {
        svgRelativePosition: {}, // relative to page : top and left should not change after init
        forceUpdateTab: [],
        dependenciesStore: useDependenciesStore(),
        uiPlanningStore: useUiPlanningStore(),
        planningStore: usePlanningStore(),
        planningLanesStore: usePlanningLanesStore(),
        subprojectsStore: useSubprojectsStore(),
      };
    },
    mounted() {
      $tableBody = null; // reset from component reuse
      $tableWrapper = null;
      this.$nextTick(() => {
        this.updateSvgRelativePosition();
      });
    },
    computed: {
      showSvg() {
        return this.planning.config?.displayDependencies;
      },
      displayCriticalPath() {
        return this.planning.config?.displayCriticalPath;
      },
      visibleLanesMap() {
        return this.planningLanesStore.getVisibleLanes({ planning: this.planning }).reduce((acc, lane) => {
          if (! lane.getHidden()) acc.set(`${lane.id}`, lane);
          return acc;
        }, new Map());
      },
      visibleElements() {
        return this.planning.elements.filter(el => el.isVisibleOnPlanning() && el.getDependencies()?.length);
      },
      subPlanningsVisibleData() {
        return this.planning.lanes.filter(lane => lane.project_id && lane.isVisibleAndNotHidden()).map((lane) => {
          const subPlanning = this.subprojectsStore.getSubprojectByLaneId(lane.id);
          if (! subPlanning) return { visibleLanesMap: new Map(), visibleElements: [] };
          const visibleLanesMap = this.planningLanesStore.getVisibleLanes({ planning: subPlanning }).reduce((acc, subLane) => {
            if (! lane.project_hidden_sublanes?.includes(subLane.o_id)) acc.set(`${subLane.id}`, subLane);
            return acc;
          }, new Map());
          return {
            visibleLanesMap,
            visibleElements: subPlanning.elements.filter(el => el.isVisibleOnPlanning() && el.getDependencies()?.length),
          };
        });
      },
      subSubPlanningsVisibleData() {
        return this.subPlanningsVisibleData.map(({ visibleLanesMap: visibleSubLanesMap }) => {
          return [...visibleSubLanesMap.values()].map((subLane) => {
            const subSubPlanning = this.subprojectsStore.getSubprojectByLaneId(subLane.id);
            if (! subSubPlanning) return { visibleLanesMap: new Map(), visibleElements: [] };
            const visibleLanesMap = this.planningLanesStore.getVisibleLanes({ planning: subSubPlanning }).reduce((acc, subSubLane) => {
              if (! subLane.project_hidden_sublanes?.includes(subSubLane.o_id)) acc.set(`${subSubLane.id}`, subSubLane);
              return acc;
            }, new Map());
            return {
              visibleLanesMap,
              visibleElements: subSubPlanning.elements.filter(el => el.isVisibleOnPlanning() && el.getDependencies()?.length),
            };
          });
        });
      },
      dependenciesLinks() {
        if (! this.svgRelativePosition.right) return null; // view not ready
        this.forceUpdateTab.forEach(() => {});

        const dependenciesLinks = [];
        this.visibleElements.forEach((el) => {
          el.getDependencies().forEach((dependency) => {
            if (! dependency.successor) return;
            const dependencyLink = this.dependenciesCoordinates(el, dependency);
            if (dependencyLink) dependenciesLinks.push(dependencyLink);
          });
        });
        this.subPlanningsVisibleData.forEach((subPlanningData) => {
          subPlanningData.visibleElements.forEach((el) => {
            el.getDependencies().forEach((dependency) => {
              if (! dependency.successor) return;
              const dependencyLink = this.dependenciesCoordinates(el, dependency, subPlanningData);
              if (dependencyLink) dependenciesLinks.push(dependencyLink);
            });
          });
        });
        this.subSubPlanningsVisibleData.forEach((subSubPlanningsData) => {
          subSubPlanningsData.forEach((subSubPlanningData) => {
            subSubPlanningData.visibleElements.forEach((el) => {
              el.getDependencies().forEach((dependency) => {
                if (! dependency.successor) return;
                const dependencyLink = this.dependenciesCoordinates(el, dependency, subSubPlanningData);
                if (dependencyLink) dependenciesLinks.push(dependencyLink);
              });
            });
          });
        });
        return dependenciesLinks;
      },
      reactiveProps() {
        return this.showSvg && this.visibleLanesMap && this.visibleElements.map(el => [el.xposition, el.getWidth(), el.modificationUser])
          && this.subSubPlanningsVisibleData // force update on subsubplannings load
          && this.subPlanningsVisibleData.map(subPlanningData => subPlanningData.visibleElements.map(el => el.getLaneId())); // force update when changing subEl laneId
      },
      displayDependenciesErrors() {
        return this.uiPlanningStore.displayDependenciesErrors;
      },
      draggingLink() { // current dragging from dependencies button
        if (! this.dependenciesStore.draggingLink) return null;
        const { el, targetEl: relatedEl, pageX, pageY } = this.dependenciesStore.draggingLink;
        const elId = el.o_id ? el.id.replace(/\./g, '\\.') : el.id;
        const elTitleNode = document.querySelector(`#el${elId} .element-title`);
        const elTitlePosition = this.boundaries2Relative(elTitleNode?.getBoundingClientRect()); // relative to document
        const elTitleTop = elTitlePosition.top - this.svgRelativePosition.top;
        const elTitleRight = elTitlePosition.right - this.svgRelativePosition.left;
        const elTitleHeight = this.getElTitleHeight(el);
        const coordinates = {
          el: {
            x: elTitleRight + (el.isType('milestone') ? 2 : 0),
            y: elTitleTop + elTitleHeight / 2,
          },
          invalid: false,
          isCriticalPath: false,
        };

        coordinates.linePath = `M ${coordinates.el.x} ${coordinates.el.y} L ${pageX + $tableWrapper.scrollLeft - this.svgRelativePosition.left} ${pageY - this.svgRelativePosition.top}`;
        if (! relatedEl) {
          return coordinates;
        }

        const { visibleTimeline = {} } = this.planning;
        coordinates.invalid = relatedEl.getStartTime().isBefore(el.getEndTime());
        coordinates.isCriticalPath = relatedEl.getStartTime().diffWithWorkdays(el.getEndTime(), 'days', visibleTimeline.workdays) == 0;

        const relatedElId = relatedEl.o_id ? relatedEl.id.replace(/\./g, '\\.') : relatedEl.id;
        const relatedElTitleNode = document.querySelector(`#el${relatedElId} .element-title`);
        const relatedElTitlePosition = this.boundaries2Relative(relatedElTitleNode?.getBoundingClientRect());
        const relatedElTitleTop = relatedElTitlePosition.top - this.svgRelativePosition.top;
        const relatedElTitleLeft = relatedElTitlePosition.left - this.svgRelativePosition.left;
        const relatedElTitleHeight = this.getElTitleHeight(relatedEl);

        coordinates.relatedEl = {
          x: relatedElTitleLeft + (relatedEl.isType('milestone') ? -2 : 0),
          y: relatedElTitleTop + relatedElTitleHeight / 2,
        };
        this.createPaths(coordinates, { relatedElTitleTop, relatedElTitleHeight });
        return coordinates;
      },
      hasSubprojects() {
        return this.planningStore.hasSubprojects;
      },
    },
    watch: {
      'planning.visibleTimeline.timelinecols': function () { // for subprojects
        this.$nextTick(() => { this.forceUpdateTab = []; });
      },
      reactiveProps() {
        setTimeout(() => { this.forceUpdateTab = []; }); // next tick not enough on subprojects load
      },
      hasSubprojects() { // show / hide sub lane headers changes svg left position
        this.updateSvgRelativePosition();
      },
    },
    methods: {
      getElTitleHeight(el) {
        if (el.isType('task')) {
          return el.getWidth() > constants.miniElementBreakWidth ? 19.2 : 11.2; // height estimation
        }
        return 15.2; // height estimation
      },
      boundaries2Relative(boundaries) {
        $tableWrapper = $tableWrapper || document.querySelector('#table-wrapper');
        if (! boundaries || ! $tableWrapper) return { top: 0, left: 0, right: 0, height: 0 };
        return {
          top: boundaries.top + window.scrollY,
          left: boundaries.left + window.scrollX + $tableWrapper.scrollLeft,
          right: boundaries.right + window.scrollX + $tableWrapper.scrollLeft,
          height: boundaries.height,
        };
      },
      updateSvgRelativePosition(counter = 0) {
        if (counter > 10) return;
        $tableBody = $tableBody || document.querySelector('.table-body');
        if ($tableBody) this.svgRelativePosition = this.boundaries2Relative($tableBody.getBoundingClientRect());
        if (! this.svgRelativePosition.right) setTimeout(() => { this.updateSvgRelativePosition(counter + 1); }, 300 * counter * counter);
      },
      createPaths(coordinates, { relatedElTitleTop, relatedElTitleHeight, sourceAnchor = 'end', targetAnchor = 'start' }) {
        const arrowSize = 4;
        const offsetTop = 6;
        const turnRightBottom = 'q 4 0 4 4';
        const turnBottomRight = 'q 0 4 4 4';
        const turnTopRight = 'q 0 -4 4 -4';
        const turnRightTop = 'q 4 0 4 -4';
        const turnTopLeft = 'q 0 -4 -4 -4';
        const turnLeftTop = 'q -4 0 -4 -4';
        const turnBottomLeft = 'q -4 0 -4 4';
        const turnLeftBottom = 'q -4 0 -4 4';

        if (sourceAnchor == 'start') { // SS
          if (coordinates.el.y == coordinates.relatedEl.y) { // same line
            coordinates.linePath = `M ${coordinates.el.x} ${coordinates.el.y} h -4`;
            coordinates.linePath += ` ${turnLeftTop} v ${-offsetTop - 4} ${turnTopRight}`;
            coordinates.linePath += ` H ${coordinates.relatedEl.x} ${turnRightBottom} V ${relatedElTitleTop - arrowSize}`;
            coordinates.arrowPath = `M ${coordinates.relatedEl.x} ${relatedElTitleTop - arrowSize} h ${arrowSize * 2} l ${-arrowSize} ${arrowSize * 1.5} Z`;
            return;
          }

          const targetIsAbove = relatedElTitleTop < coordinates.el.y;
          coordinates.linePath = `M ${coordinates.el.x} ${coordinates.el.y} h -4`;
          coordinates.linePath += targetIsAbove ? ` ${turnLeftTop} V ${coordinates.relatedEl.y + 4} ${turnTopRight}` : ` ${turnLeftBottom} V ${coordinates.relatedEl.y - 4} ${turnBottomRight}`;
          coordinates.linePath += ` H ${coordinates.relatedEl.x - arrowSize}`;
          coordinates.arrowPath = `M ${coordinates.relatedEl.x - arrowSize - 1} ${coordinates.relatedEl.y - arrowSize} v ${arrowSize * 2} l ${arrowSize * 1.5} ${-arrowSize} Z`;
          return;
        }

        if (targetAnchor == 'end') { // FF
          const arrowX = coordinates.relatedEl.x - arrowSize;
          const targetIsAbove = relatedElTitleTop < coordinates.el.y;
          const travelY = targetIsAbove
            ? Math.min(coordinates.el.y - offsetTop, relatedElTitleTop - offsetTop)
            : coordinates.el.y;
          const approachFromLeft = coordinates.el.x <= arrowX;
          const canStartRight = targetIsAbove && approachFromLeft && coordinates.el.x + 20 < arrowX;
          const elbowX = arrowX + (approachFromLeft ? -4 : 4);
          if (canStartRight) {
            coordinates.linePath = `M ${coordinates.el.x} ${coordinates.el.y} h 8 ${turnRightTop} V ${travelY + 4} ${turnTopRight} H ${elbowX}`;
          } else if (targetIsAbove) {
            coordinates.linePath = `M ${coordinates.el.x} ${coordinates.el.y} V ${travelY} H ${elbowX}`;
          } else {
            coordinates.linePath = `M ${coordinates.el.x} ${coordinates.el.y} H ${elbowX}`;
          }
          coordinates.linePath += ` ${approachFromLeft ? turnRightBottom : turnLeftBottom} V ${relatedElTitleTop}`;
          coordinates.arrowPath = `M ${arrowX - arrowSize} ${relatedElTitleTop + 2 - arrowSize} h ${arrowSize * 2} l ${-arrowSize} ${arrowSize * 1.5} Z`;
          return;
        }

        let arrow = 'left';
        if (coordinates.el.x + 20 < coordinates.relatedEl.x) { // more than 20px gap
          if (coordinates.el.y == coordinates.relatedEl.y) { // same line
            coordinates.linePath = `M ${coordinates.el.x} ${coordinates.el.y} H ${coordinates.relatedEl.x}`;
          } else if (coordinates.el.y < coordinates.relatedEl.y) { // downward
            coordinates.linePath = `M ${coordinates.el.x} ${coordinates.el.y} h 8 ${turnRightBottom} V ${coordinates.relatedEl.y - 4} ${turnBottomRight} H ${coordinates.relatedEl.x}`;
          } else { // upward
            coordinates.linePath = `M ${coordinates.el.x} ${coordinates.el.y} h 8 ${turnRightTop} V ${coordinates.relatedEl.y + 4} ${turnTopRight} H ${coordinates.relatedEl.x}`;
          }
        } else if (coordinates.el.x - 8 <= coordinates.relatedEl.x) { // forward (or almost) but elements are too close to each other
          // eslint-disable-next-line no-lonely-if
          if (coordinates.el.y == coordinates.relatedEl.y) { // same line
            coordinates.relatedEl.x += relatedElTitleHeight / 2;
            coordinates.relatedEl.y -= relatedElTitleHeight / 2;
            coordinates.linePath = `M ${coordinates.el.x} ${coordinates.el.y} V ${coordinates.relatedEl.y - offsetTop}`;
            coordinates.linePath += ` ${turnTopRight} H ${coordinates.relatedEl.x - 4} ${turnRightBottom} V ${coordinates.relatedEl.y}`;
            arrow = 'top';
          } else if (coordinates.el.y < coordinates.relatedEl.y) { // downward
            coordinates.relatedEl.x += relatedElTitleHeight / 2;
            coordinates.relatedEl.y -= relatedElTitleHeight / 2;
            coordinates.linePath = `M ${coordinates.el.x} ${coordinates.el.y} H ${coordinates.relatedEl.x - 4} ${turnRightBottom} V ${relatedElTitleTop}`;
            arrow = 'top';
          } else if (coordinates.el.x + 8 > coordinates.relatedEl.x) { // upward with less than 8px gap
            coordinates.relatedEl.x += relatedElTitleHeight / 2;
            coordinates.relatedEl.y -= relatedElTitleHeight / 2;
            coordinates.linePath = `M ${coordinates.el.x} ${coordinates.el.y} V ${coordinates.relatedEl.y - offsetTop} ${turnTopRight} H ${coordinates.relatedEl.x - 4}`;
            coordinates.linePath += ` ${turnRightBottom} V ${coordinates.relatedEl.y}`;
            arrow = 'top';
          } else { // upward with 8px - 20px gap
            coordinates.linePath = `M ${coordinates.el.x} ${coordinates.el.y} V ${coordinates.relatedEl.y + 4} ${turnTopRight} H ${coordinates.relatedEl.x}`;
          }
        } else { // backward
          coordinates.relatedEl.x += relatedElTitleHeight / 2;
          coordinates.relatedEl.y -= relatedElTitleHeight / 2;
          if (coordinates.el.y == coordinates.relatedEl.y) { // same line
            coordinates.linePath = `M ${coordinates.el.x} ${coordinates.el.y} V ${coordinates.relatedEl.y - offsetTop}`;
            coordinates.linePath += ` ${turnTopLeft} H ${coordinates.relatedEl.x + 4} ${turnLeftBottom} V ${coordinates.relatedEl.y}`;
          } else if (coordinates.el.y < coordinates.relatedEl.y) { // downward
            coordinates.linePath = `M ${coordinates.el.x} ${coordinates.el.y} V ${relatedElTitleTop - offsetTop - 4} H ${coordinates.relatedEl.x + 4} ${turnLeftBottom} V ${relatedElTitleTop}`;
          } else { // upward
            coordinates.linePath = `M ${coordinates.el.x} ${coordinates.el.y} V ${coordinates.relatedEl.y - offsetTop} ${turnTopLeft} H ${coordinates.relatedEl.x + 4}`;
            coordinates.linePath += ` ${turnBottomLeft} V ${coordinates.relatedEl.y}`;
          }
          arrow = 'top';
        }

        if (arrow == 'top') {
          coordinates.arrowPath = `M ${coordinates.relatedEl.x - arrowSize} ${relatedElTitleTop - arrowSize} h ${arrowSize * 2} l ${-arrowSize} ${arrowSize * 1.5} Z`;
        } else {
          coordinates.arrowPath = `M ${coordinates.relatedEl.x - arrowSize - 1} ${coordinates.relatedEl.y - arrowSize} v ${arrowSize * 2} l ${arrowSize * 1.5} ${-arrowSize} Z`;
        }
      },
      dependenciesCoordinates(el, dependency, subPlanningData) {
        if (! dependency.successor) return null;
        let relatedEl;
        let visibleLanesMap;
        if (subPlanningData) {
          relatedEl = subPlanningData.visibleElements.find(item => item.o_id == dependency.target_id);
          visibleLanesMap = subPlanningData.visibleLanesMap;
        } else {
          relatedEl = this.visibleElements.find(item => item.id == dependency.target_id);
          visibleLanesMap = this.visibleLanesMap;
        }
        if (! relatedEl) return null;

        const elLane = visibleLanesMap.get(`${el.getLaneId()}`);
        const relatedElLane = visibleLanesMap.get(`${relatedEl.getLaneId()}`);
        if (! elLane || ! relatedElLane) return null;

        // Detect not respected dependencies (cycle references or due to locked date)
        const { visibleTimeline = {} } = this.planning;
        const { sourceAnchor, targetAnchor } = getDependencyAnchors(dependency.type, true);
        const sourceAnchorTime = getAnchorTime(el, sourceAnchor);
        const targetAnchorTime = getAnchorTime(relatedEl, targetAnchor);
        const delay = dependency.delay || 0;
        const invalid = this.displayDependenciesErrors
          && targetAnchorTime.addWithWorkdays(-delay, 'days', visibleTimeline.workdays).isBefore(sourceAnchorTime);

        const isCriticalPath = targetAnchorTime.diffWithWorkdays(sourceAnchorTime, 'days', visibleTimeline.workdays) <= delay;

        const elId = el.o_id ? el.id.replace(/\./g, '\\.') : el.id;
        const elTitleNode = document.querySelector(`#el${elId} .element-title`);
        const elTitlePosition = this.boundaries2Relative(elTitleNode?.getBoundingClientRect()); // relative to document
        const elTitleTop = elTitlePosition.top - this.svgRelativePosition.top;
        const elTitleLeft = elTitlePosition.left - this.svgRelativePosition.left;
        const elTitleRight = elTitlePosition.right - this.svgRelativePosition.left;
        const elTitleHeight = this.getElTitleHeight(el);

        const relatedElId = relatedEl.o_id ? relatedEl.id.replace(/\./g, '\\.') : relatedEl.id;
        const relatedElTitleNode = document.querySelector(`#el${relatedElId} .element-title`);
        const relatedElTitlePosition = this.boundaries2Relative(relatedElTitleNode?.getBoundingClientRect());
        const relatedElTitleTop = relatedElTitlePosition.top - this.svgRelativePosition.top;
        const relatedElTitleLeft = relatedElTitlePosition.left - this.svgRelativePosition.left;
        const relatedElTitleRight = relatedElTitlePosition.right - this.svgRelativePosition.left;
        const relatedElTitleHeight = this.getElTitleHeight(relatedEl);

        const coordinates = {
          el: {
            x: (sourceAnchor == 'start' ? elTitleLeft : elTitleRight) + (el.isType('milestone') ? (sourceAnchor == 'start' ? -2 : 2) : 0),
            y: elTitleTop + elTitleHeight / 2,
          },
          relatedEl: {
            x: (targetAnchor == 'start' ? relatedElTitleLeft : relatedElTitleRight) + (relatedEl.isType('milestone') ? (targetAnchor == 'start' ? -2 : 2) : 0),
            y: relatedElTitleTop + relatedElTitleHeight / 2,
          },
          invalid,
          isCriticalPath,
        };

        this.createPaths(coordinates, { relatedElTitleTop, relatedElTitleHeight, sourceAnchor, targetAnchor });

        return coordinates;
      },
    },
  };
</script>
